import { DEFAULT_THEME } from "@superbright/indexeddb-orm";
import { startTransition, useEffect, useState } from "react";

import { getPropertyQuery } from "api/property/usePropertyQuery";
import { useQuery } from "utils";

import {
  applyThemeTokens,
  awaitThemeFontsReady,
  getThemeTokens,
  hasCustomThemeFonts,
  registerThemeFonts,
  resolveEmbedThemeFonts,
} from "./themeConfig";
import type { EmbedTheme } from "./types";

function applyDefaults() {
  applyThemeTokens(getThemeTokens(DEFAULT_THEME));
}

/**
 * Returns true when SSR already injected the custom theme CSS
 * (`<style data-source="vike-ssr-theme">`). Used to decide whether the client
 * still needs to apply DEFAULT_THEME: if the SSR theme is present we leave it
 * alone, otherwise we apply defaults. Called client-side only (from the effect),
 * where it reads the live DOM; the server branch is kept for completeness.
 */
function isSsrThemePresent(ssrThemeInjected?: boolean): boolean {
  if (typeof document === "undefined") return !!ssrThemeInjected;
  return !!document.querySelector("style[data-source='vike-ssr-theme']");
}

/**
 * On app boot, applies the published theme from the embed property API:
 * sets CSS variables on `:root` and loads custom fonts via the FontFace API.
 *
 * In CMS preview mode (`?mode=preview`), `cmsStyler.ts` owns theme application
 * via postMessage, so we skip injection here entirely.
 *
 * Spinner policy: the spinner is shown ONLY while a property's custom theme
 * fonts are still downloading, so the user never sees the default→custom font
 * swap. Properties with no custom fonts (no theme at all, or custom colours but
 * default fonts) render immediately — there is nothing to download and no swap
 * to hide. Theme *colours* are baked into SSR (`+Head.tsx`) and paint instantly
 * regardless, so the spinner only ever gates on fonts.
 */
export function useEmbedThemeGate(
  slug?: string,
  ssrHasCustomFonts?: boolean
): { isReady: boolean } {
  // Preview mode is a client-only (iframe) concept; on the server there is no
  // `window`, so default to false.
  const isPreview =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("mode") === "preview";

  // Show the spinner ONLY when the property has custom fonts that still need to
  // download. `ssrHasCustomFonts` is derived from the SSR data payload (passed
  // to the client verbatim), so this value is identical on the server and the
  // client's first render — no hydration mismatch. Everything else reveals
  // immediately; the effect below applies the theme and (when custom fonts are
  // present) flips `isReady` once those fonts have loaded.
  const [isReady, setIsReady] = useState(() => !ssrHasCustomFonts);

  const propertyQuery = useQuery({
    ...getPropertyQuery(slug),
    enabled: !!slug && !isPreview,
  });

  // Always apply theme + register fonts, even when SSR-seeded.
  // The gate (isReady) only controls whether children render; this effect
  // ensures fonts are loaded and CSS vars are written to :root.
  useEffect(() => {
    // Preview mode: cmsStyler.ts owns theme application via postMessage.
    if (isPreview) {
      if (!isReady) startTransition(() => setIsReady(true));
      return;
    }

    // No slug — defaults are the only thing we can apply.
    if (!slug) {
      if (!isSsrThemePresent()) applyDefaults();
      if (!isReady) startTransition(() => setIsReady(true));
      return;
    }

    // Query still in-flight: don't apply anything yet. The consumer keeps
    // showing its loader so the user never sees a flash of default theme
    // followed by the real one when the request resolves.
    if (propertyQuery.isLoading) return;

    // Fetch errored — fall back to defaults so the page still renders.
    if (propertyQuery.isError) {
      console.warn(
        "[useEmbedThemeGate] Failed to load theme, using defaults",
        propertyQuery.error
      );
      if (!isSsrThemePresent()) applyDefaults();
      if (!isReady) startTransition(() => setIsReady(true));
      return;
    }

    const embedTheme: EmbedTheme | undefined = propertyQuery.data?.data?.theme;

    // No published theme — defaults are good enough.
    if (!embedTheme || embedTheme.version === null) {
      if (!isSsrThemePresent()) applyDefaults();
      if (!isReady) startTransition(() => setIsReady(true));
      return;
    }

    // Apply the published theme (colours/sizes paint immediately).
    const themeState = resolveEmbedThemeFonts(embedTheme);
    applyThemeTokens(getThemeTokens(themeState));

    // No custom fonts → nothing to download and no swap to hide; reveal now.
    if (!hasCustomThemeFonts(themeState)) {
      if (!isReady) startTransition(() => setIsReady(true));
      return;
    }

    // SSR emits @font-face + <link rel="preload"> for custom fonts, so the
    // browser already fetches them eagerly; we only need to know when they're
    // done. When SSR did NOT emit them, load via the FontFace API instead.
    // DOM query is safe here — useEffect is client-only, never runs during SSR.
    const ssrFonts = !!document.querySelector(
      "style[data-source='vike-ssr-fonts']"
    );

    // Already revealed (SSR didn't flag custom fonts, e.g. SSR fetch failed):
    // just make sure non-SSR fonts get registered, then stop — re-hiding would
    // be jarring.
    if (isReady) {
      if (!ssrFonts) void registerThemeFonts(themeState);
      return;
    }

    // Spinner is up and the property has custom fonts: reveal only once those
    // fonts have loaded, so the default→custom font swap is never visible.
    // No timeout — the spinner stays until the fonts settle. Font load errors
    // resolve on their own (via allSettled / FontFace rejection), so a failed
    // font still reveals; only a request that never settles could keep it up.
    let cancelled = false;
    const fontsReady = ssrFonts
      ? awaitThemeFontsReady(themeState)
      : registerThemeFonts(themeState);
    void fontsReady.then(() => {
      if (!cancelled) startTransition(() => setIsReady(true));
    });
    return () => {
      cancelled = true;
    };
  }, [
    isReady,
    isPreview,
    propertyQuery.data,
    propertyQuery.error,
    propertyQuery.isError,
    propertyQuery.isLoading,
    slug,
  ]);

  return { isReady };
}
